import type { components } from "./types";

type ValidationError = components["schemas"]["ValidationError"];
type HTTPValidationError = components["schemas"]["HTTPValidationError"];

/**
 * Field-level error for form display
 */
export interface FieldError {
	field: string;
	message: string;
}

/**
 * Parsed API error response
 */
export interface ParsedApiError {
	/** General error message */
	message: string;
	/** HTTP status code */
	code?: string;
	/** Field-level errors for forms */
	fieldErrors?: FieldError[];
}

/**
 * Map FastAPI field path to form field name
 * FastAPI validation errors use paths like ["body", "email"] or ["body", "password"]
 */
function mapFieldPath(location: (string | number)[]): string {
	// location is typically: ["body", "field_name"] or ["query", "param_name"]
	// We want to extract the actual field name
	if (location.length >= 2) {
		const fieldName = location[location.length - 1];
		if (typeof fieldName === "string") {
			// Convert snake_case to camelCase for form fields
			return fieldName.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
		}
	}
	return "unknown";
}

/**
 * Parse FastAPI HTTPValidationError (422 responses)
 * Extracts field-level errors from the detail array
 */
export function parseValidationError(error: HTTPValidationError): FieldError[] {
	if (!error.detail || !Array.isArray(error.detail)) {
		return [];
	}

	return error.detail.map((validationError: ValidationError) => ({
		field: mapFieldPath(validationError.loc),
		message: validationError.msg,
	}));
}

/**
 * Parse generic API error response
 * Handles both validation errors (422) and other error responses
 */
export function parseApiError(error: unknown, response?: { status: number }): ParsedApiError {
	// Default error
	const defaultError: ParsedApiError = {
		message: "An unexpected error occurred. Please try again.",
		code: response?.status.toString(),
	};

	// Handle 422 validation errors
	if (response?.status === 422 && error && typeof error === "object") {
		const validationError = error as HTTPValidationError;
		const fieldErrors = parseValidationError(validationError);

		if (fieldErrors.length > 0) {
			return {
				message: "Please fix the errors below",
				code: "422",
				fieldErrors,
			};
		}
	}

	// Handle errors with detail field (FastAPI standard)
	if (error && typeof error === "object" && "detail" in error) {
		const detail = (error as { detail: unknown }).detail;
		if (typeof detail === "string") {
			return {
				message: detail,
				code: response?.status.toString(),
			};
		}
	}

	// Handle generic Error objects
	if (error instanceof Error) {
		return {
			message: error.message,
			code: response?.status.toString(),
		};
	}

	return defaultError;
}

/**
 * Get error message for a specific field from parsed errors
 */
export function getFieldError(
	fieldErrors: FieldError[] | undefined,
	fieldName: string
): string | undefined {
	return fieldErrors?.find((e) => e.field === fieldName)?.message;
}

/**
 * Check if a field has an error
 */
export function hasFieldError(fieldErrors: FieldError[] | undefined, fieldName: string): boolean {
	return !!getFieldError(fieldErrors, fieldName);
}
